/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
function _createMdxContent(props) {
  const _components = Object.assign({
    h2: "h2",
    p: "p",
    code: "code",
    h3: "h3",
    pre: "pre",
    a: "a"
  }, _provideComponents(), props.components);
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h2, {
      children: "现有的 CSS(Existing CSS)"
    }), "\n", _jsx(_components.p, {
      children: "¥Existing CSS"
    }), "\n", _jsx(_components.p, {
      children: "如果你选择将样式组件与现有 CSS 一起使用，则应该注意一些实现细节。"
    }), "\n", _jsx(_components.p, {
      children: "¥There are a couple of implementation details that you should be aware of, if you choose to use\nstyled-components together with existing CSS."
    }), "\n", _jsxs(_components.p, {
      children: ["styled-components 生成带有类的实际样式表，并通过 ", _jsx(_components.code, {
        children: "className"
      }), " 属性将这些类附加到样式组件的 DOM 节点。它在运行时将生成的样式表注入到文档头部的末尾。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥styled-components generates an actual stylesheet with classes, and attaches those classes to\nthe DOM nodes of styled components via the ", _jsx(_components.code, {
        children: "className"
      }), " prop.\nIt injects the generated stylesheet at the end of the head of the document during runtime."]
    }), "\n", _jsx(_components.h3, {
      children: "正常 React 组件的样式(Styling normal React components)"
    }), "\n", _jsx(_components.p, {
      children: "¥Styling normal React components"
    }), "\n", _jsxs(_components.p, {
      children: ["如果你使用 ", _jsx(_components.code, {
        children: "styled(MyComponent)"
      }), " 表示法并且 ", _jsx(_components.code, {
        children: "MyComponent"
      }), " 不渲染传入的 ", _jsx(_components.code, {
        children: "className"
      }), " 属性，则不会应用任何样式。为了避免此问题，请确保你的组件将传入的 ", _jsx(_components.code, {
        children: "className"
      }), " 连接到 DOM 节点："]
    }), "\n", _jsxs(_components.p, {
      children: ["¥If you use the ", _jsx(_components.code, {
        children: "styled(MyComponent)"
      }), " notation and ", _jsx(_components.code, {
        children: "MyComponent"
      }), " does not\nrender the passed-in ", _jsx(_components.code, {
        children: "className"
      }), " prop, then no styles will be applied.\nTo avoid this issue, make sure your component attaches the passed-in ", _jsx(_components.code, {
        children: "className"
      }), " to a DOM node:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "class MyComponent extends React.Component {\n  render() {\n    // Attach the passed-in className to the DOM node\n    return <div className={this.props.className} />\n  }\n}\n"
      })
    }), "\n", _jsx(_components.p, {
      children: "如果你的类中已有样式，则可以将全局类与传入的类合并："
    }), "\n", _jsx(_components.p, {
      children: "¥If you have pre-existing styles with a class, you can combine the global class with the\npassed-in one:"
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "class MyComponent extends React.Component {\n  render() {\n    // Attach the passed-in className to the DOM node\n    return <div className={`some-global-class ${this.props.className}`} />\n  }\n}\n"
      })
    }), "\n", _jsx(_components.h3, {
      children: "特殊性问题(Issues with specificity)"
    }), "\n", _jsx(_components.p, {
      children: "¥Issues with specificity"
    }), "\n", _jsx(_components.p, {
      children: "如果你将全局类与样式化组件类一起应用，结果可能不是你所期望的。如果在两个类中定义的属性具有相同的特异性，则最后一个将获胜。"
    }), "\n", _jsx(_components.p, {
      children: "¥If you apply a global class together with a styled component class, the result might not be\nwhat you're expecting. If a property is defined in both classes with the same specificity,\nthe last one will win."
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "// MyComponent.js\nconst MyComponent = styled.div`background-color: green;`;\n\n// my-component.css\n.red-bg {\n  background-color: red;\n}\n\n// For some reason this component still has a green background,\n// even though you're trying to override it with the \"red-bg\" class!\n<MyComponent className=\"red-bg\" />\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["在上面的示例中，样式化组件类优先于全局类，因为样式化组件默认在运行时在 ", _jsx(_components.code, {
        children: "<head>"
      }), " 末尾注入其样式。因此，它的样式胜过其他单一类名选择器。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥In the above example the styled component class takes precedence over the global class, since\nstyled-components injects its styles during runtime at the end of the ", _jsx(_components.code, {
        children: "<head>"
      }), " by default.\nThus its styles win over other single classname selectors."]
    }), "\n", _jsx(_components.p, {
      children: "一种解决方案是提高样式表中选择器的特异性："
    }), "\n", _jsx(_components.p, {
      children: "¥One solution is to bump up the specificity of the selectors in your stylesheet:"
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-css",
        children: "/* my-component.css */\n.red-bg.red-bg {\n  background-color: red;\n}\n"
      })
    }), "\n", _jsx(_components.h3, {
      children: "避免与第三方样式和脚本发生冲突(Avoiding conflicts with third-party styles and scripts)"
    }), "\n", _jsx(_components.p, {
      children: "¥Avoiding conflicts with third-party styles and scripts"
    }), "\n", _jsx(_components.p, {
      children: "如果你在不完全控制的页面上部署样式组件，则可能需要采取预防措施以确保组件样式不会与主页的样式冲突。"
    }), "\n", _jsx(_components.p, {
      children: "¥If you deploy styled-components on a page you don't fully control, you may need to take\nprecautions to ensure that your component styles don't conflict with those of the host page."
    }), "\n", _jsx(_components.p, {
      children: "最常见的问题是特异性不够。例如，考虑具有以下样式规则的主页："
    }), "\n", _jsx(_components.p, {
      children: "¥The most common problem is insufficient specificity. For example, consider a host page with this\nstyle rule:"
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-css",
        children: "body.my-body button {\n  padding: 24px;\n}\n"
      })
    }), "\n", _jsx(_components.p, {
      children: "由于该规则包含一个类名和两个标签名，因此它比此样式组件生成的单个类名选择器具有更高的特异性："
    }), "\n", _jsx(_components.p, {
      children: "¥Since the rule contains a classname and two tag names, it has higher specificity than the single\nclassname selector generated by this styled component:"
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "styled.button`\n  padding: 16px;\n`\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["没有办法让你的组件完全不受主机页样式的影响，但你至少可以使用 ", _jsx(_components.a, {
        href: "https://github.com/QuickBase/babel-plugin-styled-components-css-namespace",
        children: "babel-plugin-styled-components-css-namespace"
      }), " 提高其样式规则的特异性，它允许你为所有样式组件指定 CSS 命名空间。如果所有样式组件都在具有 ", _jsx(_components.code, {
        children: "id=\"my-widget\""
      }), " 的容器中渲染，那么一个好的命名空间将类似于 ", _jsx(_components.code, {
        children: "#my-widget"
      }), "，因为 ID 选择器比任意数量的类名具有更高的特异性。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥There's no way to give your components complete immunity from the host page's styles, but you can\nat least boost the specificity of their style rules with\n", _jsx(_components.a, {
        href: "https://github.com/QuickBase/babel-plugin-styled-components-css-namespace",
        children: "babel-plugin-styled-components-css-namespace"
      }), ",\nwhich allows you to specify a CSS namespace for all of your styled components. A good namespace\nwould be something like ", _jsx(_components.code, {
        children: "#my-widget"
      }), ", if all of your styled-components render in a container\nwith ", _jsx(_components.code, {
        children: "id=\"my-widget\""
      }), ", since ID selectors have more specificity than any number of classnames."]
    }), "\n", _jsxs(_components.p, {
      children: ["一个更罕见的问题是页面上两个样式组件实例之间的冲突。你可以通过在带有样式组件实例的代码包中定义 ", _jsx(_components.code, {
        children: "process.env.SC_ATTR"
      }), " 来避免这种情况。该值覆盖默认的 ", _jsx(_components.code, {
        children: "<style>"
      }), " 标签属性 ", _jsx(_components.code, {
        children: "data-styled"
      }), "（v3 及更低版本中的 ", _jsx(_components.code, {
        children: "data-styled-components"
      }), "），允许每个样式组件实例识别自己的标签。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥A rarer problem is conflicts between two instances of styled-components on the page. You can avoid\nthis by defining ", _jsx(_components.code, {
        children: "process.env.SC_ATTR"
      }), " in the code bundle with your styled-components instance.\nThis value overrides the default ", _jsx(_components.code, {
        children: "<style>"
      }), " tag attribute, ", _jsx(_components.code, {
        children: "data-styled"
      }), " (", _jsx(_components.code, {
        children: "data-styled-components"
      }), " in v3 and lower), allowing\neach styled-components instance to recognize its own tags."]
    })]
  });
}
function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = Object.assign({}, _provideComponents(), props.components);
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, props)
  })) : _createMdxContent(props);
}
export default MDXContent;
